import { useEffect, useMemo, useRef, useState } from "react";

interface UseTypewriterEffectProps {
  examples: string[];
  isActive: boolean;
  setCurrentIndex?: (index: number) => void;
}

export const useTypewriterEffect = ({ 
  examples, 
  isActive, 
  setCurrentIndex 
}: UseTypewriterEffectProps) => {
  const [placeholder, setPlaceholder] = useState("");
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const shuffleArray = <T>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const shuffledExamples = useMemo(() => shuffleArray(examples), [examples]);

  useEffect(() => {
    if (!isActive) {
      setPlaceholder("");
      return;
    }

    let localExampleIndex = 0;

    const typeText = (text: string, callback?: () => void) => {
      setIsTyping(true);
      let currentIndex = 0;

      const typeNextChar = () => {
        if (currentIndex <= text.length) {
          const displayText =
            text.slice(0, currentIndex) +
            (currentIndex < text.length ? "|" : "");
          setPlaceholder(displayText);
          currentIndex++;
          timeoutRef.current = setTimeout(typeNextChar, 40);
        } else {
          setPlaceholder(text);
          setIsTyping(false);
          callback?.();
        }
      };

      typeNextChar();
    };

    const eraseText = (text: string, callback?: () => void) => {
      setIsTyping(true);
      let currentIndex = text.length;

      const eraseNextChar = () => {
        if (currentIndex > 0) {
          const displayText = text.slice(0, currentIndex) + "|";
          setPlaceholder(displayText);
          currentIndex--;
          timeoutRef.current = setTimeout(eraseNextChar, 30);
        } else {
          setPlaceholder("|");
          setIsTyping(false);
          timeoutRef.current = setTimeout(() => {
            callback?.();
          }, 300);
        }
      };

      eraseNextChar();
    };

    const startTypewriterCycle = () => {
      const currentExample = shuffledExamples[localExampleIndex];

      typeText(currentExample, () => {
        timeoutRef.current = setTimeout(() => {
          eraseText(currentExample, () => {
            localExampleIndex = (localExampleIndex + 1) % shuffledExamples.length;
            setCurrentExampleIndex(localExampleIndex);
            setCurrentIndex?.(localExampleIndex);
            timeoutRef.current = setTimeout(startTypewriterCycle, 200);
          });
        }, 750);
      });
    };

    startTypewriterCycle();

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isActive, shuffledExamples, setCurrentIndex]);

  return {
    placeholder,
    currentExampleIndex,
    isTyping,
  };
}; 